import { AutocompleteMultiple, getFilterIncludeOptions } from "@msys/ui";
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from "@mui/material";
import React from "react";
import { isTagReserved, useReservedTagLabels } from "./useReservedTagLabels.js";

interface CrmTagsAutocompleteInputProps {
  inputLabel: string;
  options: string[];
  value: string[];
  onChange: (
    value: string[],
    event: React.ChangeEvent<{}>,
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string> | undefined
  ) => void;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  error?: string;
  loading?: boolean;
}

export const CrmTagsAutocompleteInput = ({
  options,
  ...props
}: CrmTagsAutocompleteInputProps) => {
  const { reservedTagLabels } = useReservedTagLabels();

  const getOptionLabel = React.useCallback(
    (option: string) => {
      return isTagReserved(option) ? reservedTagLabels[option] : option;
    },
    [reservedTagLabels]
  );

  const filterOptions = React.useMemo(
    () => getFilterIncludeOptions(getOptionLabel),
    [getOptionLabel]
  );

  // Normalize and deduplicate options to prevent duplicate keys
  const normalizedOptions = React.useMemo(() => {
    const seen = new Set<string>();
    return options.filter(option => {
      const normalized = option.trim().toLocaleUpperCase();
      if (seen.has(normalized)) {
        return false;
      }
      seen.add(normalized);
      return true;
    });
  }, [options]);

  return (
    <AutocompleteMultiple
      {...props}
      options={normalizedOptions}
      getOptionLabel={getOptionLabel}
      getOptionKey={option => option.trim().toLocaleUpperCase()}
      isOptionEqualToValue={(option, value) =>
        option.trim().toLocaleUpperCase() === value.trim().toLocaleUpperCase()
      }
      filterOptions={filterOptions}
    />
  );
};
